import { AppBar, Box, Toolbar, Typography } from '@mui/material';
import { forwardRef, type ReactNode } from 'react';

import { IconButton } from './IconButton.js';
import { Icon } from './Icon.js';
import { typography } from '../theme/tokens.js';

/**
 * FSE TopAppBar — MD3 small top app bar (M3 Expressive).
 *
 * Spec: `Documentation/Claude design files/preview/top-app-bar.html` +
 * `preview/nav-arrows.html`. 64dp tall, `colorSurface` background, level-1
 * shadow. Title uses MD3 Title Large (display family); optional back arrow
 * (a 48dp icon button) sits leading. `actions` slot trailing.
 *
 * Wraps MUI `AppBar`/`Toolbar`. Owns the public prop surface — consumers never
 * touch MUI's `sx`. Exposed through @fse/ui so consumers never import
 * @mui/material directly (swap-promise).
 */
export interface TopAppBarProps {
  /** Title text — rendered MD3 Title Large in the display family. */
  title: string;
  /** When provided, a leading back arrow (48dp icon button) invokes this. */
  onBack?: () => void;
  /** Trailing actions slot (icon buttons, etc.). */
  actions?: ReactNode;
  /** Center the title (MD3 center-aligned variant). Default leading-aligned. */
  centerTitle?: boolean;
}

export const TopAppBar = forwardRef<HTMLDivElement, TopAppBarProps>(function TopAppBar(
  { title, onBack, actions, centerTitle = false },
  ref,
) {
  return (
    <AppBar
      ref={ref}
      position="static"
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        color: 'text.primary',
        boxShadow: 'none',
        borderBottom: 'none',
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: { xs: 64, sm: 64 },
          height: 64,
          px: 0.5,
          gap: 0.5,
        }}
      >
        {onBack ? (
          <IconButton
            aria-label="Back"
            onClick={onBack}
            sx={{
              width: 48,
              height: 48,
              color: 'text.primary',
            }}
          >
            <Icon name="arrow_back" size={24} />
          </IconButton>
        ) : null}

        <Typography
          component="h1"
          noWrap
          title={title}
          sx={{
            flexGrow: 1,
            ml: onBack ? 0.5 : 1,
            fontFamily: typography.fontFamilyDisplay,
            fontSize: `${typography.titleLarge.fontSize}px`,
            fontWeight: typography.titleLarge.fontWeight,
            lineHeight: 1.1,
            // Mode-aware: matches the AppBar's `text.primary` (back arrow uses
            // the same), so the title flips with the active color mode. In
            // light this resolves to the same ink as brand.earth[900].
            color: 'text.primary',
            textAlign: centerTitle ? 'center' : 'left',
          }}
        >
          {title}
        </Typography>

        {actions ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>{actions}</Box>
        ) : null}
      </Toolbar>
    </AppBar>
  );
});
